import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { PieChart, Pie, Cell } from 'recharts';
import { sportMeta } from '../../../utils/data';

export type SportDistributionEntry = {
	sport: string;
	percentage: number;
};

interface SportDistributionChartProps {
	data: SportDistributionEntry[];
}

const PADDING_ANGLE = 2;
const MAX_OUTER_RADIUS = 95;
// Below this radius outside labels leave too little donut to read, so we switch to a legend
const MIN_OUTER_RADIUS = 52;
const INNER_RADIUS_RATIO = 0.62;
// Label leader line: radial segment from the slice edge, then a horizontal arm to the text
const ELBOW = 8;
const ARM = 16;
const TEXT_GAP = 4;
const EDGE_PADDING = 4;
// Rough average glyph width relative to font size, used to reserve room for label text
const CHAR_WIDTH_RATIO = 0.6;
const RADIAN = Math.PI / 180;

const getSportColor = (sport: string) =>
	sportMeta[sport]?.color || sportMeta['Default']?.color || '#6b7280';

const formatPercentage = (value: number) => `${(Number(value) || 0).toFixed(1)}%`;

const useElementSize = () => {
	const [element, setElement] = useState<HTMLDivElement | null>(null);
	const [size, setSize] = useState({ width: 0, height: 0 });

	useEffect(() => {
		if (!element) return;
		const update = () => {
			const width = element.clientWidth;
			const height = element.clientHeight;
			setSize((prev) =>
				prev.width === width && prev.height === height ? prev : { width, height }
			);
		};
		update();
		if (typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(update);
		observer.observe(element);
		return () => observer.disconnect();
	}, [element]);

	return [setElement, size] as const;
};

// Mirrors how recharts lays out sectors (counter-clockwise from 3 o'clock, padding between
// non-zero slices) so label positions can be resolved for all slices before rendering
const getMidAngles = (data: SportDistributionEntry[]) => {
	const values = data.map((entry) => Math.max(Number(entry.percentage) || 0, 0));
	const sum = values.reduce((acc, value) => acc + value, 0);
	const nonZeroCount = values.filter((value) => value !== 0).length;
	const totalAngle = 360 - nonZeroCount * PADDING_ANGLE;
	let prevEnd = 0;
	return values.map((value, index) => {
		const start = index > 0 ? prevEnd + (value !== 0 ? PADDING_ANGLE : 0) : 0;
		const end = start + (sum > 0 ? (value / sum) * totalAngle : 0);
		prevEnd = end;
		return (start + end) / 2;
	});
};

// Pushes labels on one side of the donut apart vertically so they never overlap,
// while keeping them inside [top, bottom]
const spreadLabels = (
	items: { index: number; y: number }[],
	spacing: number,
	top: number,
	bottom: number
) => {
	const sorted = [...items].sort((a, b) => a.y - b.y);
	for (let i = 0; i < sorted.length; i++) {
		const min = i === 0 ? top : sorted[i - 1].y + spacing;
		sorted[i].y = Math.max(sorted[i].y, min);
	}
	for (let i = sorted.length - 1; i >= 0; i--) {
		const max = i === sorted.length - 1 ? bottom : sorted[i + 1].y - spacing;
		sorted[i].y = Math.min(sorted[i].y, max);
	}
	return sorted;
};

const SportDistributionChart: React.FC<SportDistributionChartProps> = ({ data }) => {
	const [wrapperRef, wrapperSize] = useElementSize();
	const [chartRef, chartSize] = useElementSize();

	const fontSize = wrapperSize.width < 380 ? 11 : 12;
	const lineHeight = Math.round(fontSize * 1.2);
	const labelBlockHeight = lineHeight * 2;
	const labelSpacing = labelBlockHeight + 4;

	// Decided from the wrapper size, which does not change when the legend toggles,
	// so the layout can't oscillate between modes
	const layout = useMemo(() => {
		const { width, height } = wrapperSize;
		if (!width || !height) return null;

		const labelWidth =
			Math.max(
				...data.map((entry) =>
					Math.max(entry.sport.length, formatPercentage(entry.percentage).length)
				),
				0
			) *
			fontSize *
			CHAR_WIDTH_RATIO;

		const radiusByWidth = width / 2 - labelWidth - ARM - TEXT_GAP - EDGE_PADDING;
		const radiusByHeight = height / 2 - ELBOW - labelBlockHeight / 2 - EDGE_PADDING;
		const outerRadius = Math.min(MAX_OUTER_RADIUS, radiusByWidth, radiusByHeight);

		const midAngles = getMidAngles(data);
		const rightCount = midAngles.filter((angle) => Math.cos(angle * RADIAN) >= 0).length;
		const maxPerSide = Math.max(rightCount, data.length - rightCount);
		const labelsFit = maxPerSide * labelSpacing <= height - EDGE_PADDING * 2;

		return { useLegend: outerRadius < MIN_OUTER_RADIUS || !labelsFit, outerRadius, midAngles };
	}, [data, wrapperSize, fontSize, labelBlockHeight, labelSpacing]);

	const useLegend = layout?.useLegend ?? false;
	const outerRadius = useLegend
		? Math.max(Math.min(MAX_OUTER_RADIUS, Math.min(chartSize.width, chartSize.height) / 2 - 6), 0)
		: (layout?.outerRadius ?? 0);
	const innerRadius = outerRadius * INNER_RADIUS_RATIO;

	const labelYByIndex = useMemo(() => {
		const result: Record<number, number> = {};
		if (!layout || useLegend || !chartSize.height) return result;
		const cy = chartSize.height / 2;
		const top = labelBlockHeight / 2 + EDGE_PADDING;
		const bottom = chartSize.height - labelBlockHeight / 2 - EDGE_PADDING;
		const left: { index: number; y: number }[] = [];
		const right: { index: number; y: number }[] = [];
		layout.midAngles.forEach((angle, index) => {
			const y = cy - (outerRadius + ELBOW) * Math.sin(angle * RADIAN);
			(Math.cos(angle * RADIAN) >= 0 ? right : left).push({ index, y });
		});
		[left, right].forEach((side) =>
			spreadLabels(side, labelSpacing, top, bottom).forEach(({ index, y }) => {
				result[index] = y;
			})
		);
		return result;
	}, [layout, useLegend, chartSize.height, outerRadius, labelBlockHeight, labelSpacing]);

	const renderLabel = ({ cx, cy, midAngle, index }: any) => {
		const entry = data[index];
		if (!entry?.sport) return null;

		const cos = Math.cos(-midAngle * RADIAN);
		const sin = Math.sin(-midAngle * RADIAN);
		const isRight = cos >= 0;
		const startX = cx + outerRadius * cos;
		const startY = cy + outerRadius * sin;
		const elbowX = cx + (outerRadius + ELBOW) * cos;
		const elbowY = cy + (outerRadius + ELBOW) * sin;
		const labelY = labelYByIndex[index] ?? elbowY;
		const armX = cx + (isRight ? 1 : -1) * (outerRadius + ARM);
		const textX = armX + (isRight ? TEXT_GAP : -TEXT_GAP);

		return (
			<g style={{ pointerEvents: 'none' }}>
				<polyline
					points={`${startX},${startY} ${elbowX},${elbowY} ${armX},${labelY}`}
					fill="none"
					stroke="#9ca3af"
					strokeWidth={1}
				/>
				<text
					x={textX}
					y={labelY}
					textAnchor={isRight ? 'start' : 'end'}
					fontSize={fontSize}
					fill="#1f2937"
					style={{ fontFamily: 'inherit' }}
				>
					<tspan x={textX} dy={-lineHeight / 2 + fontSize * 0.35}>
						{entry.sport}
					</tspan>
					<tspan x={textX} dy={lineHeight} fontWeight={600}>
						{formatPercentage(entry.percentage)}
					</tspan>
				</text>
			</g>
		);
	};

	return (
		<Box
			ref={wrapperRef}
			sx={{
				width: '100%',
				height: '100%',
				minWidth: 0,
				minHeight: 0,
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}
		>
			<Box ref={chartRef} sx={{ position: 'relative', flex: 1, minHeight: 0, width: '100%' }}>
				{layout && chartSize.width > 0 && chartSize.height > 0 && outerRadius > 0 && (
					<PieChart width={chartSize.width} height={chartSize.height}>
						<Pie
							data={data}
							cx="50%"
							cy="50%"
							labelLine={false}
							label={useLegend ? false : renderLabel}
							outerRadius={outerRadius}
							innerRadius={innerRadius}
							dataKey="percentage"
							nameKey="sport"
							paddingAngle={PADDING_ANGLE}
						>
							{data.map((entry, index) => {
								const sportColor = getSportColor(entry.sport || '');
								return (
									<Cell
										key={`cell-${index}`}
										fill={sportColor}
										stroke={sportColor}
										strokeWidth={1.5}
									/>
								);
							})}
						</Pie>
					</PieChart>
				)}
				{innerRadius > 0 && (
					<Box
						sx={{
							position: 'absolute',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							width: `${Math.max(innerRadius * 2 - 12, 0)}px`,
							textAlign: 'center',
							pointerEvents: 'none',
						}}
					>
						<Typography
							variant="caption"
							sx={{
								display: 'block',
								fontSize: innerRadius < 45 ? '11px' : '13px',
								fontWeight: 600,
								color: '#333',
								lineHeight: 1.3,
							}}
						>
							Game Distribution
						</Typography>
					</Box>
				)}
			</Box>

			{useLegend && (
				<Box
					component="ul"
					sx={{
						listStyle: 'none',
						m: 0,
						p: '6px 4px 0',
						display: 'flex',
						flexWrap: 'wrap',
						justifyContent: 'center',
						columnGap: 1.5,
						rowGap: 0.5,
						flexShrink: 0,
						maxHeight: '45%',
						overflowY: 'auto',
					}}
				>
					{data.map((entry, index) => (
						<Box
							component="li"
							key={`legend-${index}`}
							sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '12px' }}
						>
							<Box
								component="span"
								sx={{
									width: 10,
									height: 10,
									borderRadius: '50%',
									flexShrink: 0,
									backgroundColor: getSportColor(entry.sport || ''),
								}}
							/>
							<Box component="span" sx={{ color: '#1f2937', whiteSpace: 'nowrap' }}>
								{entry.sport} <strong>{formatPercentage(entry.percentage)}</strong>
							</Box>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};

export default SportDistributionChart;
